// src/pages/bookings/BookingsPage.jsx
import React, { useState, useEffect } from "react";
import {
    Container,
    Card,
    Table,
    Button,
    Form,
    Row,
    Col,
    InputGroup,
    Alert,
    Spinner,
    Modal,
} from "react-bootstrap";
import {
    FaPlus,
    FaEdit,
    FaTrash,
    FaEye,
    FaSearch,
    FaFilter,
    FaTimes,
    FaBook,
    FaCheckCircle,
    FaClock,
    FaBan,
    FaInfoCircle,
    FaUser,
    FaTruck,
    FaUserCheck,
    FaHourglassHalf,
    FaEllipsisV,
    FaExchangeAlt,
    FaCalendarAlt,
    FaMoneyBillWave,
    FaGasPump,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import {
    getBookings,
    deleteBooking,
    updateBookingStatus,
    cancelBooking,
    getBookingStatistics,
} from "../../components/services/bookingService";
import BookingStatusBadge from "../../components/bookings/BookingStatusBadge";
import "../../styles/styles.css";

// Custom toast helper
const showCustomToast = (type, message, autoClose = 3000) => {
    const configs = {
        success: { color: "#28a745", border: "2px solid #28a745" },
        error: { color: "#dc3545", border: "2px solid #dc3545" },
        warning: { color: "#ffc107", border: "2px solid #ffc107" },
        info: { color: "#17a2b8", border: "2px solid #17a2b8" },
    };

    const config = configs[type];
    toast[type](message, {
        position: "top-right",
        autoClose: autoClose,
        transition: Bounce,
        style: {
            border: config.border,
            background: "transparent",
            color: config.color,
            boxShadow: "none",
            marginTop: "70px",
        },
        icon: false,
    });
};

const BookingsPage = () => {
    const navigate = useNavigate();

    const [bookings, setBookings] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [sourceFilter, setSourceFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState("");
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [stats, setStats] = useState(null);

    const STATUS_CONFIG = {
        booked: { bg: "#FEF6D7", color: "#FED229", label: "Booked", icon: <FaClock /> },
        assigned: { bg: "#D3EAFF", color: "#437EF7", label: "Assigned", icon: <FaUserCheck /> },
        out_for_delivery: { bg: "#FFE4E6", color: "#E11D48", label: "Out for Delivery", icon: <FaTruck /> },
        delivered: { bg: "#ECFDF3", color: "#027A48", label: "Delivered", icon: <FaCheckCircle /> },
        cancelled: { bg: "#FFDCE2", color: "#F94765", label: "Cancelled", icon: <FaBan /> },
        pending: { bg: "#F1F3F5", color: "#6B7280", label: "Pending", icon: <FaHourglassHalf /> },
    };

    useEffect(() => {
        loadBookings();
        loadStats();
    }, []);

    const loadBookings = async () => {
        setLoading(true);
        setErrorMessage("");
        try {
            const data = await getBookings();
            setBookings(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error("Failed to load bookings:", error);
            setErrorMessage(error.response?.data?.message || "Failed to load bookings");
            showCustomToast("error", "Failed to load bookings");
            setBookings([]);
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const data = await getBookingStatistics();
            setStats(data);
        } catch (error) {
            console.error("Failed to load stats:", error);
        }
    };

    const handleView = (booking) => {
        setSelectedBooking(booking);
        setShowViewModal(true);
        setActiveDropdown(null);
    };

    const handleEdit = (id) => {
        navigate(`/bookings/edit/${id}`);
        setActiveDropdown(null);
    };

    const handleAdd = () => navigate("/bookings/add");

    const handleDelete = async (id, bookingNumber) => {
        setActiveDropdown(null);
        const result = await Swal.fire({
            title: "Are you sure?",
            text: `Delete booking "${bookingNumber}"?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
        });

        if (result.isConfirmed) {
            try {
                await deleteBooking(id);
                showCustomToast("success", `✅ Booking "${bookingNumber}" deleted successfully!`);
                await loadBookings();
                await loadStats();
            } catch (error) {
                showCustomToast("error", error.response?.data?.message || "Failed to delete booking");
            }
        }
    };

    const handleStatusAction = async (id, action, bookingNumber) => {
        setActiveDropdown(null);
        try {
            let successMessage;

            switch (action) {
                case "assign":
                    const { value: deliveryBoyId } = await Swal.fire({
                        title: "Assign Delivery Boy",
                        input: "text",
                        inputPlaceholder: "Enter delivery boy ID",
                        showCancelButton: true,
                        confirmButtonText: "Assign",
                        cancelButtonText: "Cancel",
                    });
                    if (!deliveryBoyId) return;
                    await updateBookingStatus(id, { status: "assigned", assigned_to_delivery_boy: deliveryBoyId });
                    successMessage = `✅ Booking "${bookingNumber}" assigned successfully!`;
                    break;
                case "out_for_delivery":
                    await updateBookingStatus(id, { status: "out_for_delivery" });
                    successMessage = `✅ Booking "${bookingNumber}" is out for delivery!`;
                    break;
                case "delivered":
                    await updateBookingStatus(id, { status: "delivered" });
                    successMessage = `✅ Booking "${bookingNumber}" delivered successfully!`;
                    break;
                case "cancel":
                    const { value: reason } = await Swal.fire({
                        title: "Enter Cancellation Reason",
                        input: "text",
                        inputPlaceholder: "Reason for cancellation",
                        showCancelButton: true,
                        confirmButtonText: "Cancel",
                        cancelButtonText: "Back",
                    });
                    if (!reason) return;
                    await cancelBooking(id, { cancellation_reason: reason });
                    successMessage = `✅ Booking "${bookingNumber}" cancelled!`;
                    break;
                default:
                    return;
            }

            showCustomToast("success", successMessage);
            await loadBookings();
            await loadStats();
        } catch (error) {
            showCustomToast("error", error.response?.data?.message || `Failed to ${action} booking`);
        }
    };

    const filteredBookings = bookings.filter((booking) => {
        const customerName = booking.customer_name || "";
        const customerMobile = booking.customer_mobile || "";

        const matchesSearch =
            searchTerm === "" ||
            booking.booking_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            customerMobile.includes(searchTerm);
        const matchesStatus = statusFilter === "all" || booking.booking_status === statusFilter;
        const matchesSource = sourceFilter === "all" || booking.booking_source === sourceFilter;
        return matchesSearch && matchesStatus && matchesSource;
    });

    const clearFilters = () => {
        setSearchTerm("");
        setStatusFilter("all");
        setSourceFilter("all");
        showCustomToast("info", "Filters cleared", 2000);
    };

    const statsData = stats || {
        total: bookings.length,
        booked: bookings.filter((b) => b.booking_status === "booked").length,
        assigned: bookings.filter((b) => b.booking_status === "assigned").length,
        out_for_delivery: bookings.filter((b) => b.booking_status === "out_for_delivery").length,
        delivered: bookings.filter((b) => b.booking_status === "delivered").length,
        cancelled: bookings.filter((b) => b.booking_status === "cancelled").length,
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-IN", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount || 0);
    };

    const getStatusBadge = (status) => {
        const config = STATUS_CONFIG[status?.toLowerCase()] || STATUS_CONFIG.pending;
        return <BookingStatusBadge status={status} />;
    };

    if (loading) {
        return (
            <Container fluid className="p-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
                <ToastContainer position="top-right" autoClose={3000} transition={Bounce} />
                <div className="text-center py-5">
                    <Spinner animation="border" variant="secondary" size="lg" />
                    <h4 className="mt-3">Loading bookings...</h4>
                </div>
            </Container>
        );
    }

    return (
        <Container fluid className="px-4 py-3" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
            <ToastContainer position="top-right" autoClose={3000} transition={Bounce} />

            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="fw-bold mb-1" style={{ color: "rgb(30, 58, 111)" }}>
                        <FaBook className="me-2" /> Booking Management
                    </h2>
                    <p className="text-muted">Manage all LPG cylinder bookings and deliveries</p>
                </div>
                <Button
                    onClick={handleAdd}
                    style={{
                        backgroundColor: "rgb(30, 58, 111)",
                        border: "none",
                        borderRadius: "14px",
                        padding: "12px 22px",
                        fontWeight: "600",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                    }}
                >
                    <FaPlus size={14} /> New Booking
                </Button>
            </div>

            <Row className="g-3 mb-4">
                <StatCard title="Total Bookings" value={statsData.total} icon={<FaBook size={20} style={{ color: "#437EF7" }} />} bg="#D3EAFF" />
                <StatCard title="Booked" value={statsData.booked} icon={<FaClock size={20} style={{ color: "#FED229" }} />} bg="#FEF6D7" />
                <StatCard title="Assigned" value={statsData.assigned} icon={<FaUserCheck size={20} style={{ color: "#437EF7" }} />} bg="#D3EAFF" />
                <StatCard title="Out for Delivery" value={statsData.out_for_delivery} icon={<FaTruck size={20} style={{ color: "#E11D48" }} />} bg="#FFE4E6" />
                <StatCard title="Delivered" value={statsData.delivered} icon={<FaCheckCircle size={20} style={{ color: "#027A48" }} />} bg="#ECFDF3" />
                <StatCard title="Cancelled" value={statsData.cancelled} icon={<FaBan size={20} style={{ color: "#F94765" }} />} bg="#FFDCE2" />
            </Row>

            {errorMessage && (
                <Alert variant="danger" className="mb-4" onClose={() => setErrorMessage("")} dismissible>
                    {errorMessage}
                </Alert>
            )}

            <Card className="border-0 shadow-sm mb-4 rounded-3">
                <Card.Body className="py-3">
                    <Row>
                        <Col md={4}>
                            <InputGroup>
                                <InputGroup.Text style={{ backgroundColor: "#fff", borderRight: "none" }}>
                                    <FaSearch className="text-muted" />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder="Search by booking no or customer..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    style={{ borderLeft: "none" }}
                                />
                                {searchTerm && (
                                    <Button variant="outline-secondary" onClick={() => setSearchTerm("")}>
                                        <FaTimes />
                                    </Button>
                                )}
                            </InputGroup>
                        </Col>
                        <Col md={3}>
                            <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                <option value="all">All Status</option>
                                <option value="booked">Booked</option>
                                <option value="assigned">Assigned</option>
                                <option value="out_for_delivery">Out for Delivery</option>
                                <option value="delivered">Delivered</option>
                                <option value="cancelled">Cancelled</option>
                            </Form.Select>
                        </Col>
                        <Col md={3}>
                            <Form.Select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
                                <option value="all">All Sources</option>
                                <option value="walk-in">Walk-in</option>
                                <option value="phone">Phone</option>
                                <option value="online">Online</option>
                                <option value="app">Mobile App</option>
                            </Form.Select>
                        </Col>
                        <Col md={2}>
                            <Button variant="outline-secondary" onClick={clearFilters} className="w-100">
                                <FaFilter /> Clear
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <div className="d-flex justify-content-between align-items-center mb-3">
                <p className="text-muted mb-0">
                    Showing {filteredBookings.length} of {bookings.length} booking records
                </p>
            </div>

            <Card className="border-0 shadow-sm rounded-3" style={{ overflow: "hidden" }}>
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table hover className="mb-0" style={{ fontSize: "14px" }}>
                            <thead style={{ backgroundColor: "#f8f9fa", borderBottom: "2px solid #dee2e6" }}>
                                <tr>
                                    <th style={{ padding: "16px 12px" }}>Booking No</th>
                                    <th style={{ padding: "16px 12px" }}>Customer</th>
                                    <th style={{ padding: "16px 12px" }}>Cylinder Type</th>
                                    <th style={{ padding: "16px 12px" }}>Qty</th>
                                    <th style={{ padding: "16px 12px" }}>Delivery Date</th>
                                    <th style={{ padding: "16px 12px" }}>Status</th>
                                    <th style={{ padding: "16px 12px", width: "60px" }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredBookings.length > 0 ? (
                                    filteredBookings.map((booking) => (
                                        <TableRow
                                            key={booking.id}
                                            booking={booking}
                                            onView={handleView}
                                            onEdit={handleEdit}
                                            onDelete={handleDelete}
                                            onStatusAction={handleStatusAction}
                                            formatDate={formatDate}
                                            formatCurrency={formatCurrency}
                                            getStatusBadge={getStatusBadge}
                                            activeDropdown={activeDropdown}
                                            setActiveDropdown={setActiveDropdown}
                                        />
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center py-5">
                                            <div className="py-4">
                                                <FaBook size={50} className="text-muted mb-3" />
                                                <h5 className="text-muted">No bookings found</h5>
                                                <p className="text-muted small">Click 'New Booking' to create your first booking</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
            </Card>

            <BookingViewModal
                show={showViewModal}
                onHide={() => setShowViewModal(false)}
                booking={selectedBooking}
                onEdit={handleEdit}
                formatDate={formatDate}
                formatCurrency={formatCurrency}
                getStatusBadge={getStatusBadge}
            />
        </Container>
    );
};

// ==============================================
// Stat Card Component
// ==============================================
const StatCard = ({ title, value, icon, bg }) => (
    <Col md={2}>
        <Card className="border-0 shadow-sm rounded-3">
            <Card.Body className="py-3 px-3">
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <small className="text-muted mb-0" style={{ fontSize: "11px" }}>{title}</small>
                        <h3 className="fw-bold mb-0" style={{ fontSize: "24px" }}>{value}</h3>
                    </div>
                    <div style={{ backgroundColor: bg, padding: "10px", borderRadius: "12px" }}>{icon}</div>
                </div>
            </Card.Body>
        </Card>
    </Col>
);

// ==============================================
// Table Row Component
// ==============================================
const TableRow = ({
    booking,
    onView,
    onEdit,
    onDelete,
    onStatusAction,
    formatDate,
    formatCurrency,
    getStatusBadge,
    activeDropdown,
    setActiveDropdown,
}) => {
    const isOpen = activeDropdown === booking.id;

    return (
        <tr style={{ borderBottom: "1px solid #e9ecef" }}>
            <td style={{ padding: "16px 12px" }}>
                <div className="fw-semibold" style={{ color: "rgb(30, 58, 111)" }}>
                    {booking.booking_number || "N/A"}
                </div>
            </td>
            <td style={{ padding: "16px 12px" }}>
                <div className="fw-semibold">{booking.customer_name || "N/A"}</div>
                <small className="text-muted">{booking.customer_mobile || ""}</small>
            </td>
            <td style={{ padding: "16px 12px" }}>
                <small>{booking.cylinder_type_name || "N/A"}</small>
            </td>
            <td style={{ padding: "16px 12px" }}>
                <strong>{booking.quantity || 1}</strong>
            </td>
            <td style={{ padding: "16px 12px" }}>{formatDate(booking.preferred_delivery_date)}</td>
            <td style={{ padding: "16px 12px" }}>{getStatusBadge(booking.booking_status)}</td>
            <td style={{ padding: "16px 12px" }}>
                <div className="action-dropdown">
                    <button
                        className="action-trigger"
                        onClick={() => setActiveDropdown(isOpen ? null : booking.id)}
                        style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "2px",
                            width: "24px",
                            height: "24px",
                            borderRadius: "4px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <circle cx="12" cy="6" r="2" fill="currentColor" />
                            <circle cx="12" cy="12" r="2" fill="currentColor" />
                            <circle cx="12" cy="18" r="2" fill="currentColor" />
                        </svg>
                    </button>
                    {isOpen && (
                        <div className="dropdown-menu-custom">
                            <button onClick={() => { setActiveDropdown(null); onView(booking); }} className="dropdown-item-custom">
                                <FaEye style={{ color: "#4361ee", fontSize: "12px" }} /> View
                            </button>
                            <button onClick={() => { setActiveDropdown(null); onEdit(booking.id); }} className="dropdown-item-custom">
                                <FaEdit style={{ color: "#ff9800", fontSize: "12px" }} /> Edit
                            </button>
                            <button onClick={() => { setActiveDropdown(null); onDelete(booking.id, booking.booking_number); }} className="dropdown-item-custom delete">
                                <FaTrash style={{ color: "#dc3545", fontSize: "12px" }} /> Delete
                            </button>
                            {booking.booking_status === "booked" && (
                                <>
                                    <button onClick={() => { setActiveDropdown(null); onStatusAction(booking.id, "assign", booking.booking_number); }} className="dropdown-item-custom">
                                        <FaUserCheck style={{ color: "#437EF7", fontSize: "12px" }} /> Assign
                                    </button>
                                    <button onClick={() => { setActiveDropdown(null); onStatusAction(booking.id, "cancel", booking.booking_number); }} className="dropdown-item-custom delete">
                                        <FaBan style={{ color: "#dc3545", fontSize: "12px" }} /> Cancel
                                    </button>
                                </>
                            )}
                            {booking.booking_status === "assigned" && (
                                <>
                                    <button onClick={() => { setActiveDropdown(null); onStatusAction(booking.id, "out_for_delivery", booking.booking_number); }} className="dropdown-item-custom">
                                        <FaTruck style={{ color: "#E11D48", fontSize: "12px" }} /> Out for Delivery
                                    </button>
                                    <button onClick={() => { setActiveDropdown(null); onStatusAction(booking.id, "cancel", booking.booking_number); }} className="dropdown-item-custom delete">
                                        <FaBan style={{ color: "#dc3545", fontSize: "12px" }} /> Cancel
                                    </button>
                                </>
                            )}
                            {booking.booking_status === "out_for_delivery" && (
                                <button onClick={() => { setActiveDropdown(null); onStatusAction(booking.id, "delivered", booking.booking_number); }} className="dropdown-item-custom">
                                    <FaCheckCircle style={{ color: "#28a745", fontSize: "12px" }} /> Deliver
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </td>
        </tr>
    );
};

// ==============================================
// Booking View Modal Component
// ==============================================
// ==============================================
// Booking View Modal Component - Complete
// ==============================================
const BookingViewModal = ({
    show,
    onHide,
    booking,
    onEdit,
    formatDate,
    formatCurrency,
    getStatusBadge,
}) => {
    if (!booking) return null;

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton className="bg-dark text-white rounded-top-3 border-0">
                <Modal.Title className="fw-bold">
                    <FaEye className="me-2" /> Booking Details
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4" style={{ maxHeight: "80vh", overflowY: "auto" }}>
                {/* Row 1: Booking + Customer Information */}
                <Row className="mb-4">
                    <Col md={6}>
                        <InfoCard title="Booking Information" icon={<FaBook />}>
                            <InfoRow label="Booking Number" value={booking.booking_number || "N/A"} highlight />
                            <InfoRow label="Booking Date" value={formatDate(booking.booking_date)} />
                            <InfoRow label="Preferred Delivery" value={formatDate(booking.preferred_delivery_date)} />
                            <InfoRow label="Delivery Slot" value={booking.delivery_slot || "Anytime"} />
                            <InfoRow label="Status" value={getStatusBadge(booking.booking_status)} isHtml />
                        </InfoCard>
                    </Col>
                    <Col md={6}>
                        <InfoCard title="Customer Information" icon={<FaUser />}>
                            <InfoRow label="Name" value={booking.customer_name || "N/A"} highlight />
                            <InfoRow label="Mobile" value={booking.customer_mobile || "N/A"} />
                            <InfoRow label="Email" value={booking.customer_email || "N/A"} />
                            <InfoRow label="Address" value={booking.customer_address || "N/A"} />
                        </InfoCard>
                    </Col>
                </Row>

                {/* Row 2: Cylinder + Delivery Information */}
                <Row className="mb-4">
                    <Col md={6}>
                        <InfoCard title="Cylinder Details" icon={<FaGasPump />}>
                            <InfoRow label="Cylinder Type" value={booking.cylinder_type_name || "N/A"} />
                            <InfoRow label="Quantity" value={booking.quantity || 1} />
                            <InfoRow label="Unit Price" value={`₹${formatCurrency(booking.unit_price)}`} />
                            <InfoRow label="Total Amount" value={`₹${formatCurrency(booking.total_amount)}`} highlight />
                        </InfoCard>
                    </Col>
                    <Col md={6}>
                        <InfoCard title="Delivery Information" icon={<FaTruck />}>
                            <InfoRow label="Assigned To" value={booking.delivery_boy_name || "Not Assigned"} />
                            <InfoRow label="Assigned At" value={formatDate(booking.assigned_at)} />
                            <InfoRow label="Delivered At" value={formatDate(booking.delivered_at)} />
                            <InfoRow label="Booking Source" value={booking.booking_source || "Walk-in"} />
                        </InfoCard>
                    </Col>
                </Row>

                {/* Row 3: Additional Info (conditional) */}
                {booking.delivery_instructions && (
                    <Row className="mb-4">
                        <Col md={12}>
                            <InfoCard title="Delivery Instructions" icon={<FaInfoCircle />}>
                                <p className="mb-0">{booking.delivery_instructions}</p>
                            </InfoCard>
                        </Col>
                    </Row>
                )}

                {booking.notes && (
                    <Row className="mb-4">
                        <Col md={12}>
                            <InfoCard title="Notes" icon={<FaInfoCircle />}>
                                <p className="mb-0">{booking.notes}</p>
                            </InfoCard>
                        </Col>
                    </Row>
                )}
            </Modal.Body>
            <Modal.Footer className="bg-white rounded-bottom-3 border-top">
                <Button variant="outline-secondary" onClick={onHide} style={{ borderRadius: "10px", padding: "8px 20px" }}>
                    Close
                </Button>
                <Button
                    variant="primary"
                    onClick={() => { onHide(); onEdit(booking.id); }}
                    style={{ borderRadius: "10px", padding: "8px 20px" }}
                >
                    <FaEdit className="me-2" /> Edit
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// ==============================================
// Helper Components
// ==============================================
const InfoCard = ({ title, icon, children }) => (
    <Card className="border-0 shadow-sm rounded-3 h-100">
        <Card.Header className="bg-white border-bottom pt-3 pb-2">
            <h6 className="fw-bold mb-0" style={{ color: "rgb(30, 58, 111)" }}>{icon} {title}</h6>
        </Card.Header>
        <Card.Body>{children}</Card.Body>
    </Card>
);

const InfoRow = ({ label, value, isHtml, highlight }) => (
    <div className="mb-2 pb-2 border-bottom">
        <small className="text-muted">{label}</small>
        <div className={highlight ? "fw-bold fs-5" : ""} style={highlight ? { color: "rgb(30, 58, 111)" } : {}}>
            {isHtml ? value : <span>{value}</span>}
        </div>
    </div>
);

// Styles
const styles = document.createElement("style");
styles.textContent = `
  .rounded-3 { border-radius: 0.75rem !important; }
  .table tbody tr:hover { background-color: #f8f9fa; }
  .action-dropdown { position: relative; }
  .action-trigger { color: #64748b; transition: all 0.2s; }
  .action-trigger:hover { color: #1e293b; background-color: #f1f5f9; }
  .dropdown-menu-custom {
    position: absolute; top: 100%; right: 0; margin-top: 4px; min-width: 150px;
    background: white; border-radius: 8px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 1000; overflow: hidden; display: flex; flex-direction: column; gap: 2px; padding: 4px;
  }
  .dropdown-item-custom {
    display: flex; align-items: center; gap: 8px; padding: 8px 12px;
    border: none; background: white; cursor: pointer; transition: background-color 0.2s;
    border-radius: 6px; font-size: 13px; width: 100%; text-align: left;
  }
  .dropdown-item-custom:hover { background-color: #f8fafc; }
  .dropdown-item-custom.delete:hover { background-color: #fef2f2; }
  .form-control:focus, .form-select:focus { border-color: rgb(30, 58, 111); box-shadow: 0 0 0 0.2rem rgba(30, 58, 111, 0.25); }
  .btn-primary { background-color: rgb(30, 58, 111); border-color: rgb(30, 58, 111); }
  .btn-primary:hover { background-color: #1e3a6f; border-color: #1e3a6f; }
  .col-md-2 { flex: 0 0 16.666%; max-width: 16.666%; }
  @media (max-width: 767px) {
    .col-md-2 { flex: 0 0 50%; max-width: 50%; }
  }
`;
document.head.appendChild(styles);

export default BookingsPage;